import React, { useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import {
  Button,
  DatePicker,
  Flex,
  Input,
  Select,
  Spin,
  Table,
  Tooltip,
  Typography,
} from 'antd'
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { FormattedMessage, injectIntl } from 'react-intl'
import dayjs from 'dayjs'
import {
  useGetIssues,
  useLinkProblemLogToIssue,
  useFetchIssueCategories,
} from '../hooks/Issues'
import { useFaultByProblemLogId } from '../hooks/Faults'
import { useVehicleTestById } from '../hooks/VehicleTests'
import { useCheckPermission } from '../hooks/Permissions'
import { requireAuthentication } from '../helpers/authentication'
import { filterOptionByLabel } from '../helpers/utils'
import './styles.scss'

const { Title } = Typography
const { RangePicker } = DatePicker

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'OPENED' },
  { value: 'closed', label: 'CLOSED' },
]

const CLEAN_POINT_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
]

const DEFAULT_FILTERS = {
  search: '',
  status: 'all',
  cleanPoint: 'all',
  dateRange: null,
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PageHeader({ onGoBack }) {
  return (
    <Flex align="center" gap="middle" className="associate-issue-header">
      <Button icon={<ArrowLeftOutlined />} shape="round" onClick={onGoBack}>
        <FormattedMessage id="productionLineDashboard.associateIssue.goBack" />
      </Button>
      <Title level={4} style={{ margin: 0 }}>
        <FormattedMessage id="productionLineDashboard.associateIssue.title" />
      </Title>
    </Flex>
  )
}

function IssueSearchBar({ filters, onChange, intl }) {
  return (
    <Flex gap="small" align="center">
      <Input
        placeholder={intl.formatMessage({
          id: 'productionLineDashboard.associateIssue.searchPlaceholder',
        })}
        value={filters.search}
        onChange={(e) => onChange({ ...filters, search: e.target.value })}
        style={{ flex: 1 }}
      />
      <Button icon={<SearchOutlined />} onClick={() => onChange(filters)} />
    </Flex>
  )
}

function IssueFilters({ filters, onChange, intl }) {
  return (
    <Flex gap="large" style={{ marginTop: 16 }}>
      <div>
        <div>
          <FormattedMessage id="productionLineDashboard.associateIssue.issueStatus" />{' '}
          :
        </div>
        <Select
          value={filters.status}
          options={STATUS_OPTIONS}
          onChange={(v) => onChange({ ...filters, status: v })}
          style={{ width: 200 }}
        />
      </div>

      <div>
        <div>
          <FormattedMessage id="productionLineDashboard.associateIssue.hasCleanPoint" />{' '}
          :
        </div>
        <Select
          value={filters.cleanPoint}
          options={CLEAN_POINT_OPTIONS}
          onChange={(v) => onChange({ ...filters, cleanPoint: v })}
          style={{ width: 200 }}
        />
      </div>

      <div>
        <div>
          <FormattedMessage id="productionLineDashboard.associateIssue.createdAt" />{' '}
          :
        </div>
        <RangePicker
          value={filters.dateRange}
          onChange={(v) => onChange({ ...filters, dateRange: v })}
          placeholder={[
            intl.formatMessage({
              id: 'productionLineDashboard.associateIssue.startDate',
            }),
            intl.formatMessage({
              id: 'productionLineDashboard.associateIssue.endDate',
            }),
          ]}
        />
      </div>
    </Flex>
  )
}

function IssueTable({
  issues,
  isLoading,
  hasSearchTerm,
  selectedId,
  onSelect,
  intl,
}) {
  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: intl.formatMessage({
        id: 'productionLineDashboard.associateIssue.columns.description',
      }),
      dataIndex: 'description',
    },
    {
      title: intl.formatMessage({
        id: 'productionLineDashboard.associateIssue.columns.cleanPoint',
      }),
      dataIndex: 'clean_point',
      render: (v) => v || '—',
    },
    {
      title: intl.formatMessage({
        id: 'productionLineDashboard.associateIssue.columns.status',
      }),
      dataIndex: 'status',
    },
    {
      title: intl.formatMessage({
        id: 'productionLineDashboard.associateIssue.columns.createdAt',
      }),
      dataIndex: 'created_at',
      render: (v) => (v ? dayjs(v).format('MM/DD/YYYY h:mma') : '—'),
    },
  ]

  const rowSelection = {
    type: 'radio',
    selectedRowKeys: selectedId != null ? [selectedId] : [],
    onChange: (keys) => onSelect(keys[0] ?? null),
  }

  return (
    <Table
      rowKey="id"
      rowSelection={rowSelection}
      columns={columns}
      dataSource={issues}
      loading={isLoading}
      style={{ marginTop: 24 }}
      pagination={{ pageSize: 10, hideOnSinglePage: true }}
      locale={{
        emptyText: hasSearchTerm
          ? intl.formatMessage({
              id: 'productionLineDashboard.associateIssue.noIssuesFound',
              defaultMessage: 'No issues found',
            })
          : intl.formatMessage({
              id: 'productionLineDashboard.associateIssue.searchToSeeIssues',
              defaultMessage: 'Search to see issues',
            }),
      }}
    />
  )
}

function CreateNewIssueSection({ makeModelId, hasProblemLog, onCreate, intl }) {
  const [categoryId, setCategoryId] = useState(null)
  const { categories } = useFetchIssueCategories({ make_model_id: makeModelId })
  const categoryOptions = (categories ?? []).map(
    ({ id, code, description }) => ({
      value: id,
      label: `${code} — ${description}`,
    }),
  )

  const needsNote = !hasProblemLog
  const needsCategory = categoryId == null
  const isDisabled = needsNote || needsCategory

  let disabledReason
  if (needsNote && needsCategory) {
    disabledReason = intl.formatMessage({
      id: 'productionLineDashboard.associateIssue.createNewIssueNeedsNoteAndCategory',
      defaultMessage:
        'Add a note and select a category before creating an issue',
    })
  } else if (needsNote) {
    disabledReason = intl.formatMessage({
      id: 'productionLineDashboard.associateIssue.createNewIssueNeedsNote',
      defaultMessage: 'Add a note before creating an issue',
    })
  } else if (needsCategory) {
    disabledReason = intl.formatMessage({
      id: 'productionLineDashboard.associateIssue.createNewIssueNeedsCategory',
      defaultMessage: 'Select a category before creating an issue',
    })
  }

  return (
    <div className="create-new-issue-section" style={{ marginBottom: 32 }}>
      <Title level={5}>
        <FormattedMessage id="productionLineDashboard.associateIssue.createNewIssue" />
      </Title>
      <Flex gap="small" align="flex-end">
        <div>
          <div>
            <FormattedMessage id="productionLineDashboard.associateIssue.selectCategory" />
            {' :'}
          </div>
          <Select
            style={{ width: 300 }}
            showSearch
            options={categoryOptions}
            value={categoryId}
            onChange={setCategoryId}
            placeholder={intl.formatMessage({
              id: 'productionLineDashboard.associateIssue.selectCategoryPlaceholder',
              defaultMessage: 'Select Category',
            })}
            filterOption={filterOptionByLabel}
          />
        </div>
        {/* Tooltip wraps a span, not the Button directly -- antd disabled
            buttons don't reliably fire the hover events Tooltip listens for. */}
        <Tooltip title={isDisabled ? disabledReason : undefined}>
          <span>
            <Button
              type="primary"
              disabled={isDisabled}
              onClick={() => onCreate(categoryId)}
            >
              <FormattedMessage id="productionLineDashboard.associateIssue.createNewIssue" />
            </Button>
          </span>
        </Tooltip>
      </Flex>
    </div>
  )
}

function PageFooter({
  selectedId,
  hasProblemLog,
  onAttach,
  onClear,
  isAttaching,
  intl,
}) {
  const needsSelection = selectedId == null
  const needsNote = !hasProblemLog
  const canAttach = !needsSelection && !needsNote

  let disabledReason
  if (needsSelection && needsNote) {
    disabledReason = intl.formatMessage({
      id: 'productionLineDashboard.associateIssue.attachSelectedNeedsNoteAndSelection',
      defaultMessage: 'Add a note and select an issue before attaching',
    })
  } else if (needsNote) {
    disabledReason = intl.formatMessage({
      id: 'productionLineDashboard.associateIssue.attachSelectedNeedsNote',
      defaultMessage: 'Add a note before attaching to an issue',
    })
  } else if (needsSelection) {
    disabledReason = intl.formatMessage({
      id: 'productionLineDashboard.associateIssue.attachSelectedNeedsSelection',
      defaultMessage: 'Select an issue to attach',
    })
  }

  return (
    <Flex gap="small" className="associate-issue-footer">
      {/* Tooltip wraps a span, not the Button directly -- antd disabled
          buttons don't reliably fire the hover events Tooltip listens for. */}
      <Tooltip title={!canAttach ? disabledReason : undefined}>
        <span>
          <Button
            disabled={!canAttach || isAttaching}
            loading={isAttaching}
            onClick={onAttach}
          >
            <FormattedMessage id="productionLineDashboard.associateIssue.attachSelected" />
          </Button>
        </span>
      </Tooltip>
      <Button onClick={onClear} disabled={isAttaching}>
        <FormattedMessage id="productionLineDashboard.associateIssue.clearAll" />
      </Button>
    </Flex>
  )
}

// ─── Filtering logic ──────────────────────────────────────────────────────────

function applySearch(issues, search) {
  if (!search) return issues
  const lower = search.toLowerCase()
  return issues.filter((issue) => {
    const matchesDesc = issue.description?.toLowerCase().includes(lower)
    const matchesId = String(issue.id).toLowerCase().includes(lower)
    return matchesDesc || matchesId
  })
}

function applyFilters(issues, { status, cleanPoint, dateRange }) {
  return issues.filter((issue) => {
    if (status !== 'all' && issue.status !== status) return false

    const hasCleanPoint = !!issue.clean_point
    if (cleanPoint === 'yes' && !hasCleanPoint) return false
    if (cleanPoint === 'no' && hasCleanPoint) return false

    if (dateRange?.[0] && dateRange?.[1]) {
      const createdAt = dayjs(issue.created_at)
      if (
        createdAt.isBefore(dateRange[0], 'day') ||
        createdAt.isAfter(dateRange[1], 'day')
      ) {
        return false
      }
    }

    return true
  })
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function AssociateIssuePage({ intl }) {
  const history = useHistory()
  const location = useLocation()
  // `vehicleTestId` is present when this page matched the Production Line
  // Dashboard's route, `problemLogId` when it matched the Incident List's --
  // only one of the two params exists on any given route match, so this is a
  // reliable way to tell the two origins apart even before data has loaded
  // (unlike checking `fault`/`vehicleTest`, which start out undefined).
  const { vehicleTestId, problemLogId } = useParams()
  const isVehicleTestOrigin = vehicleTestId !== undefined

  const vehicleTestFromState = location.state?.vehicleTest
  // `fault` is present when reached from the Incident List (see IncidentList.js);
  // `vehicleTest` is present when reached from the Production Line Dashboard.
  const faultFromState = location.state?.fault

  // location.state is empty on a page refresh or a shared link -- fall back
  // to fetching by the route param in that case.
  const { vehicleTest: fetchedVehicleTest, isLoading: isLoadingVehicleTest } =
    useVehicleTestById(
      !vehicleTestFromState && isVehicleTestOrigin ? vehicleTestId : null,
    )
  const { fault: fetchedFault, isLoading: isLoadingFault } =
    useFaultByProblemLogId(
      !faultFromState && !isVehicleTestOrigin ? problemLogId : null,
    )

  const vehicleTest = vehicleTestFromState ?? fetchedVehicleTest ?? null
  const fault = faultFromState ?? fetchedFault ?? null
  const isResolvingSource = isVehicleTestOrigin
    ? !vehicleTestFromState && isLoadingVehicleTest
    : !faultFromState && isLoadingFault

  const problem_log_id = fault
    ? fault.id
    : vehicleTest?.repair_note_problem_log_id ?? null
  const makeModelId = fault
    ? fault.make_model_id
    : vehicleTest?.make_model_id ?? null
  const goBackPath = isVehicleTestOrigin
    ? '/production-line-dashboard'
    : '/incidents'

  const checkPermission = useCheckPermission()
  // Same permission Reports.js already gates its own "create issue from a
  // fault" affordance on -- also used here to hide the filters, since without
  // create access there's nothing to filter for beyond the raw list needed
  // to attach a problem log to an existing issue.
  const canCreateIssues = checkPermission('issues:create')

  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [selectedIssueId, setSelectedIssueId] = useState(null)

  // There are no filters other than the search bar itself, so an unfiltered
  // fetch would pull in every issue for the make_model -- only fetch once the
  // user has actually typed something to search for.
  const hasSearchTerm = filters.search.trim().length > 0

  const { issues = [], isLoading } = useGetIssues({
    make_model_id: makeModelId,
    enabled: hasSearchTerm,
  })
  const { mutate: linkToIssue, isLoading: isAttaching } =
    useLinkProblemLogToIssue()

  const filteredIssues = useMemo(() => {
    // Once disabled, react-query keeps serving whatever was last fetched
    // rather than clearing it -- so if the user clears the search box after
    // searching, this guard keeps the table empty instead of falling back to
    // showing that stale full list.
    if (!hasSearchTerm) return []
    const searched = applySearch(issues, filters.search)
    return canCreateIssues ? applyFilters(searched, filters) : searched
  }, [issues, filters, canCreateIssues, hasSearchTerm])

  const handleAttach = () => {
    if (selectedIssueId == null || !problem_log_id) return
    linkToIssue(
      { issue_id: selectedIssueId, pl_id: problem_log_id },
      { onSuccess: () => history.push(goBackPath) },
    )
  }

  const handleClearAll = () => {
    setFilters(DEFAULT_FILTERS)
    setSelectedIssueId(null)
  }

  // Consistent with the rest of this page: vehicleTest and fault carry the
  // same repair_note_* fields, so either can be the note's source depending
  // on where we were reached from.
  const noteSource = vehicleTest ?? fault
  const hasExistingNote = !!noteSource?.repair_note_problem_log_id
  const noteAnnotation = noteSource?.repair_note_annotation
  const noteDtcs = noteSource?.repair_note_dtcs ?? []

  const handleCreateNewIssue = (issueCategoryId) => {
    // AddNewIssue.js expects a `fault`-shaped object; from the Production
    // Line Dashboard all we actually have is the vehicleTest and its repair
    // note, so build the same shape from those (mirroring noteSource above).
    const faultForCreate =
      fault ??
      (problem_log_id != null
        ? {
            id: problem_log_id,
            make_model_id: makeModelId,
            type: 'other',
            reason: noteAnnotation,
            note: noteAnnotation,
            DTCs: noteDtcs.map((code) => ({ dtc: code, acronym: '' })),
          }
        : null)
    if (!faultForCreate) return
    // Pass the original vehicleTest through too (unchanged -- nothing's been
    // saved yet) so AddNewIssue.js's "Go Back"/submit can return here rather
    // than always landing in the Incident List's associate flow.
    history.push(`/incidents/associate/${problem_log_id}/new-issue`, {
      fault: faultForCreate,
      vehicleTest,
      issueCategoryId,
    })
  }

  const handleNoteClick = () => {
    const sourceType = isVehicleTestOrigin ? 'vehicle-test' : 'fault'
    history.push(
      `/production-line-dashboard/repair-note/${sourceType}/${noteSource.id}`,
      {
        vehicleTest,
        fault,
        existingProblemLogId: noteSource?.repair_note_problem_log_id,
        existingAnnotation: noteAnnotation,
        existingDtcs: noteDtcs,
      },
    )
  }

  if (isResolvingSource) {
    return (
      <div id="associate-issue-page">
        <Flex justify="center" style={{ marginTop: 48 }}>
          <Spin />
        </Flex>
      </div>
    )
  }

  return (
    <div id="associate-issue-page">
      <PageHeader onGoBack={() => history.push(goBackPath)} />

      <div className="associate-issue-body">
        {canCreateIssues && (
          <CreateNewIssueSection
            makeModelId={makeModelId}
            hasProblemLog={problem_log_id != null}
            onCreate={handleCreateNewIssue}
            intl={intl}
          />
        )}

        {(vehicleTest || fault) && (
          <>
            {hasExistingNote && (
              <div style={{ marginBottom: 16 }}>
                <Typography.Text strong>
                  <FormattedMessage
                    id="productionLineDashboard.associateIssue.existingNote"
                    defaultMessage="Existing Note"
                  />
                  {':'}
                </Typography.Text>
                {noteAnnotation && (
                  <Typography.Paragraph
                    style={{ margin: '4px 0 0' }}
                    ellipsis={{ rows: 2, tooltip: noteAnnotation }}
                  >
                    {noteAnnotation}
                  </Typography.Paragraph>
                )}
                {noteDtcs.length > 0 && (
                  <Typography.Text type="secondary">
                    {'DTCs: '}
                    {noteDtcs.join(', ')}
                  </Typography.Text>
                )}
              </div>
            )}

            <Button
              icon={<FileTextOutlined />}
              style={{ marginBottom: 24 }}
              onClick={handleNoteClick}
            >
              {hasExistingNote ? (
                <FormattedMessage
                  id="productionLineDashboard.associateIssue.editNote"
                  defaultMessage="Edit Note"
                />
              ) : (
                <FormattedMessage id="productionLineDashboard.associateIssue.addNote" />
              )}
            </Button>
          </>
        )}

        <Title level={5}>
          <FormattedMessage id="productionLineDashboard.associateIssue.selectIssue" />
        </Title>

        <IssueSearchBar filters={filters} onChange={setFilters} intl={intl} />

        {canCreateIssues && (
          <IssueFilters filters={filters} onChange={setFilters} intl={intl} />
        )}

        <IssueTable
          issues={filteredIssues}
          isLoading={isLoading}
          hasSearchTerm={hasSearchTerm}
          selectedId={selectedIssueId}
          onSelect={setSelectedIssueId}
          intl={intl}
        />
      </div>

      <PageFooter
        selectedId={selectedIssueId}
        hasProblemLog={problem_log_id != null}
        onAttach={handleAttach}
        onClear={handleClearAll}
        isAttaching={isAttaching}
        intl={intl}
      />
    </div>
  )
}

export default requireAuthentication(injectIntl(AssociateIssuePage))
